/**
 * The two questions asked before a ledger leaves this device.
 *
 * **Signing out uploads, then clears** (SPEC §11.1). Almost always that is the
 * whole story and nothing is asked. The leave sheet appears only when rows are
 * pending that could not upload — offline, a dead session, or a failed push —
 * and offers Try again, Cancel, and, separately and in the danger tone, "Sign
 * out and discard N changes".
 *
 * **Switching accounts never merges.** When a different account signs in over a
 * ledger with changes its own (dead) session never uploaded, the switch sheet
 * offers "Sign in as {previous} to upload" or "Discard N changes and continue as
 * {new}". With nothing pending the switch is silent, because the other account's
 * rows are all on the server already.
 *
 * Both are `ConfirmSheet`s — the app's one confirmation idiom, no card (D-1) —
 * and both replaced a `window.confirm`. The copy is plain: this is somebody's
 * money, so there is no myth here (D-7), and the count is in every question,
 * because "you have unsaved changes" is not something anyone can weigh. The
 * count is "changes", not "expenses": it sums dirty rows across every synced
 * table, and a brand-new account's eight seeded categories are not expenses.
 */

import { useEffect } from "react";

import { type ConfirmAction, ConfirmSheet } from "@/features/common/confirm_sheet";
import type { LeaveBlockReason } from "@/lib/session";

const changes = (count: number): string => `${String(count)} ${count === 1 ? "change" : "changes"}`;
const haveOrHas = (count: number): string => (count === 1 ? "has" : "have");

/**
 * While a sheet is up, it owns the keyboard. Capture phase, and every key
 * stops here: the numpad below binds digits, Enter (save) and Escape on
 * `window`, so a keystroke leaking through would log an expense behind a
 * question about losing them. Same rule as `ReauthSheet`.
 */
function useSheetKeys(onCancel: () => void): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      event.stopPropagation();
      if (event.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, [onCancel]);
}

const LEAVE_TITLES: Record<LeaveBlockReason, string> = {
  offline: "You're offline",
  expired: "Your session has ended",
  failed: "Couldn't upload your changes",
  "family-unknown": "Couldn't count family changes",
};

function leaveBody(reason: LeaveBlockReason, pending: number): string {
  if (reason === "family-unknown") return "Family changes may remain on this device, but could not be counted. Try again before signing out.";
  const what = `${changes(pending)} ${haveOrHas(pending)} not reached your account yet.`;
  if (reason === "offline") {
    return `${what} Signing out erases this device's copy, and those with it. Reconnect and try again to keep them.`;
  }
  if (reason === "expired") {
    return `${what} They cannot upload until you sign in again. Signing out now erases them.`;
  }
  return `${what} Signing out now erases them. Try again to upload them first.`;
}

export function LeaveSheet({
  pending,
  reason,
  busy,
  onRetry,
  onSignInAgain,
  onDiscard,
  onCancel,
}: {
  pending: number;
  reason: LeaveBlockReason;
  busy: boolean;
  onRetry: () => void;
  onSignInAgain: () => void;
  onDiscard: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  useSheetKeys(onCancel);

  // With a dead session, "Try again" would fail the same way every time; the
  // remedy that actually uploads the rows is signing back in.
  const first: ConfirmAction =
    reason === "expired"
      ? { label: "Sign in again", tone: "neutral", onSelect: onSignInAgain }
      : { label: busy ? "Trying…" : "Try again", tone: "neutral", onSelect: onRetry };

  return (
    <ConfirmSheet
      title={LEAVE_TITLES[reason]}
      body={leaveBody(reason, pending)}
      actions={[
        first,
        { label: `Sign out and discard ${changes(pending)}`, tone: "danger", onSelect: onDiscard },
      ]}
      onCancel={onCancel}
    />
  );
}

export function SwitchAccountSheet({
  pending,
  previousEmail,
  incomingEmail,
  onSignInAsPrevious,
  onDiscard,
  onCancel,
}: {
  pending: number;
  previousEmail: string | null;
  incomingEmail: string;
  onSignInAsPrevious: () => void;
  onDiscard: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  useSheetKeys(onCancel);
  const previous = previousEmail !== null && previousEmail !== "" ? previousEmail : null;

  return (
    <ConfirmSheet
      title="Changes from another account"
      body={
        `${changes(pending)} made as ${previous ?? "the previous account"} ${haveOrHas(pending)} ` +
        `not uploaded, and that session has ended. Accounts are never merged, so continuing as ` +
        `${incomingEmail} erases them from this device.`
      }
      actions={[
        {
          label: `Sign in as ${previous ?? "that account"} to upload`,
          tone: "neutral",
          onSelect: onSignInAsPrevious,
        },
        {
          label: `Discard ${changes(pending)} and continue as ${incomingEmail}`,
          tone: "danger",
          onSelect: onDiscard,
        },
      ]}
      onCancel={onCancel}
    />
  );
}

/**
 * Once, after an Apple sign-in created a new account. Someone who used Google
 * before has just got a second, empty ledger; accounts are never matched by
 * email, so the way back is theirs to take, and it is spelled out (SPEC §11.1).
 */
export function NewAccountSheet({ onDismiss }: { onDismiss: () => void }): React.JSX.Element {
  useSheetKeys(onDismiss);
  return (
    <ConfirmSheet
      openHaptic={false}
      title="New account"
      body="This is a new, empty Tide Tally account. Used Google before? Sign out, continue with Google, then link Apple in Settings › Account."
      actions={[{ label: "Got it", tone: "neutral", onSelect: onDismiss }]}
      onCancel={onDismiss}
      cancelLabel="Close"
    />
  );
}
